import { CommonModule } from '@angular/common';
import { Component, effect, ElementRef, inject, Input, ViewChild } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { BlahModule, BlahService } from '@headpower/blah-ng';
import { FavoritesService } from './favorites.service';
import { ToasterService } from '@headpower/layout';

@Component({
    selector: 'app-favorites',
    standalone: true,
    imports: [
        CommonModule,
        MatMenuModule,
        MatIconModule,
        MatCheckboxModule,
        BlahModule
    ],
    templateUrl: './favorites.component.html',
    styleUrl: './favorites.component.scss'
})
export class FavoritesComponent {
    private readonly favoritesService: FavoritesService = inject(FavoritesService);
    private readonly blahService = inject(BlahService);
    private readonly toaster = inject(ToasterService);

    @ViewChild('newListElementInput') public newListElementInput: ElementRef<HTMLInputElement>;
    @Input() public instruction: any;
    public currentUserId: string;

    public openedInstructionItem: any = null;
    public favoriteLists: any = [];
    public createNewListElement: boolean = false;

    constructor() {
        effect(() => {
            this.favoriteLists = this.favoritesService.favoriteLists();
            this.refreshFavoriteLists();
        });

        this.currentUserId = this.favoritesService.currentUser()['userId'];
    }

    ngOnInit() {
        this.openedInstructionItem = {
            Name: this.instruction.attributes.name,
            Url: this.instruction.attributes.path.alias
        }

        this.refreshFavoriteLists();
    }

    private refreshFavoriteLists() {
        if (!this.openedInstructionItem?.Url) return;

        this.favoriteLists.forEach(list => {
            list.selected = list.Favorites.some((fav: any) => fav.Url === this.openedInstructionItem.Url);
        });
    }

    public createNewList() {
        this.createNewListElement = true;
        setTimeout(() => this.newListElementInput?.nativeElement?.focus());
    }

    public createUserFavoriteList(confirm: boolean) {
        if (confirm && this.newListElementInput?.nativeElement?.value) {
            const newListElement = {
                Name: this.newListElementInput.nativeElement.value,
                Type: "IWI_Instruction"
            }

            this.favoritesService.createUserFavoriteList(newListElement).subscribe({
                next: (response) => {
                    this.favoriteLists.unshift({ ...response, selected: true });
                    this.createUserFavoriteToList(response);
                }, error: (error) => {
                    this.toaster.create(this.blahService.blah('error.favorites.createUserFavoriteList'));
                }
            });
        }

        this.createNewListElement = false;
    }

    public handleSelectList(event: any, listElement: any) {
        listElement.selected = event.checked;

        if (event.checked) {
            this.createUserFavoriteToList(listElement);
        } else {
            this.deleteUserFavoriteFromList(listElement);
        }
    }

    public createUserFavoriteToList(listElement: any) {
        const favoriteItem = {
            Type: 'IWI_Instruction',
            Name: this.openedInstructionItem.Name,
            Url: this.openedInstructionItem.Url,
            UserFavoriteListId: listElement.Id,
        }

        const request = listElement.UserId === this.currentUserId
            ? this.favoritesService.createUserFavoriteToList(favoriteItem)
            : this.favoritesService.createUserFavoriteToSharedList(favoriteItem);

        request.subscribe({
            next: (response) => {
                this.favoriteLists.find((list: any) => list.Id === listElement.Id)?.Favorites.push(response);
                this.favoritesService.setFavoriteLists(this.favoriteLists);
            }, error: (error) => {
                this.toaster.create(this.blahService.blah('error.favorites.createUserFavoriteToList'));
            }
        });
    }

    deleteUserFavoriteFromList(listElement: any) {
        const favoriteItem = {
            UserFavoriteListId: listElement.Id,
            UserFavoriteId: this.getCorrectUserFavoriteId(listElement.Id)
        };

        const request = listElement.UserId === this.currentUserId
            ? this.favoritesService.deleteUserFavoriteFromList(favoriteItem)
            : this.favoritesService.deleteUserFavoriteFromSharedList(favoriteItem);

        request.subscribe({
            next: (response) => {
                const list = this.favoriteLists.find((list: any) => list.Id === listElement.Id);
                if (list) {
                    list.Favorites = list.Favorites.filter((fav: any) => fav.Id !== favoriteItem.UserFavoriteId);
                }

                this.favoritesService.setFavoriteLists(this.favoriteLists);
            }, error: (error) => {
                this.toaster.create(this.blahService.blah('error.favorites.deleteUserFavoriteFromList'));
            }
        });
    }

    public getCorrectUserFavoriteId(listId: string) {
        const list = this.favoriteLists.find((list: any) => list.Id === listId);
        if (list) {
            const favorite = list.Favorites.find((fav: any) => fav.Url === this.openedInstructionItem.Url);
            return favorite ? favorite.Id : null;
        }
    }

    public hasFavorites(): boolean {
        return this.favoriteLists.some(list => list.selected);
    }
}
